import { useEffect as useEffectSC, useRef as useRefSC, useState as useStateSC } from "react";
import { Api } from "./api";
import { Icon } from "./components";

// Sub-Communities — interest groups inside an HOA.
// Issue #216 slice 2: list + create + request-to-join + (placeholder)
// detail. Bulletin posts ship in slice 3, group chat in slice 4,
// moderation surfaces in slice 5.

function _relTimeSC(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Group-chat timestamp: exact Eastern-time wall clock instead of the
// relative "20m ago" format that the rest of the sub-community UI
// uses. Per spec — when reading back through a continuous chat,
// "5/22 @ 12:20 PM" is more useful than "20m ago, 21m ago, 23m ago"
// because the relative times all blur together. Intl.DateTimeFormat
// with timeZone="America/New_York" handles EST↔EDT switching
// automatically (winter is UTC-5, summer is UTC-4); do NOT hardcode
// an offset.
function _estTimeSC(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const datePart = d.toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    month: "numeric",
    day: "numeric",
  });
  const timePart = d.toLocaleTimeString("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} @ ${timePart}`;
}

// `created_at` and `updated_at` are both `default=utcnow` on the model
// and the defaults fire independently at insert time, so fresh posts
// can have sub-millisecond drift between the two. A strict !==
// comparison flagged every brand-new post as "edited" (codex P2 on
// PR #220). Require at least a second of separation before showing
// the badge — a real edit moves updated_at by far more than that.
const EDIT_THRESHOLD_MS = 1000;
function _wasEditedSC(post) {
  if (!post.updated_at || !post.created_at) return false;
  const delta = new Date(post.updated_at).getTime() - new Date(post.created_at).getTime();
  return delta > EDIT_THRESHOLD_MS;
}


// Top-level dispatcher. App.jsx routes "sub-communities" → this, plus
// "sub-community-<public_id>" (handled inside SubCommunities by checking
// the route prop).
function SubCommunities({ user, openId, onOpenId, onMe }) {
  const [view, setView] = useStateSC(openId ? "detail" : "list");
  const [openPublicId, setOpenPublicId] = useStateSC(openId || null);

  // When the sidebar nav-clicks a different group, route + state stay
  // in sync. The list view re-shows when openId clears.
  useEffectSC(() => {
    if (openId) {
      setOpenPublicId(openId);
      setView("detail");
    } else {
      setOpenPublicId(null);
      setView("list");
    }
  }, [openId]);

  const openGroup = (id) => {
    setOpenPublicId(id);
    setView("detail");
    if (onOpenId) onOpenId(id);
  };
  const backToList = () => {
    setOpenPublicId(null);
    setView("list");
    if (onOpenId) onOpenId(null);
  };
  const openNew = () => { setView("new"); };

  if (view === "new") {
    return (
      <SubCommunityNew
        user={user}
        onCancel={backToList}
        onCreated={(grp) => { if (onMe) onMe(); openGroup(grp.id); }}
      />
    );
  }
  if (view === "detail" && openPublicId) {
    return (
      <SubCommunityDetail
        user={user}
        publicId={openPublicId}
        onBack={backToList}
        onMembershipChange={() => { if (onMe) onMe(); }}
      />
    );
  }
  return (
    <SubCommunitiesList
      user={user}
      onOpen={openGroup}
      onNew={openNew}
      onJoined={() => { if (onMe) onMe(); }}
    />
  );
}


// ── List / browse ────────────────────────────────────────────────────

function SubCommunitiesList({ user, onOpen, onNew, onJoined }) {
  // searchInput = what the user is typing right now (re-renders every
  // keystroke). q = the debounced value we actually filter on. Same
  // pattern as Town Square — list is small enough to filter client-side
  // for now, so q stays local; if growth pressure forces a server-side
  // query the API helper already accepts it (we just don't pass it yet).
  const [searchInput, setSearchInput] = useStateSC("");
  const [q, setQ] = useStateSC("");
  const [state, setState] = useStateSC({ loading: true, error: null, groups: [], quota: null });

  useEffectSC(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    window.Api.subCommunities()
      .then((data) => setState({
        loading: false,
        error: null,
        groups: data.groups || [],
        quota: data.quota || null,
      }))
      .catch((err) => setState({
        loading: false,
        error: err.message || "Couldn't load groups.",
        groups: [],
        quota: null,
      }));
  };
  useEffectSC(() => { load(); }, []);

  // Filter client-side. Match against name + description for now —
  // matches the spec ("see groups you might be interested in").
  const lowerQ = q.toLowerCase();
  const filtered = q
    ? state.groups.filter((g) =>
        g.name.toLowerCase().includes(lowerQ)
        || (g.description || "").toLowerCase().includes(lowerQ),
      )
    : state.groups;

  // Sort: joined first (creator > moderator > member), then newest.
  // Sidebar already gives users a direct link; this just keeps the
  // browse view friendly when they have a few groups they care about.
  const sorted = [...filtered].sort((a, b) => {
    const aJ = a.viewer.is_member ? 1 : 0;
    const bJ = b.viewer.is_member ? 1 : 0;
    if (aJ !== bJ) return bJ - aJ;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const quota = state.quota || { created_by_me: 0, max_per_user: 5 };
  const atCap = quota.created_by_me >= quota.max_per_user;

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 920 }}>
        <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 className="display" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>
              All Sub-Communities
            </h1>
            <div className="text-sm text-mute" style={{ marginTop: 4 }}>
              Interest groups in your HOA. Join one or start your own.
            </div>
          </div>
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <span className="text-sm text-mute" title="You can create up to 5 groups in this community.">
              {quota.created_by_me}/{quota.max_per_user} created
            </span>
            <button
              className="btn btn--primary"
              onClick={onNew}
              disabled={atCap}
              title={atCap
                ? `You've created ${quota.max_per_user} groups already. Remove or deactivate one to free a slot.`
                : "Start a new group"
              }
            >
              <Icon name="spark" size={14} /> Start a group
            </button>
          </div>
        </div>

        <div className="sc-search">
          <Icon name="search" size={16} />
          <input
            type="search"
            className="sc-search__input"
            placeholder="Search by name or description"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search sub-communities"
          />
          {searchInput && (
            <button
              type="button"
              className="sc-search__clear"
              onClick={() => setSearchInput("")}
              aria-label="Clear search"
            >×</button>
          )}
        </div>

        {state.loading && (
          <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
            Loading groups…
          </div>
        )}
        {state.error && (
          <div className="card card--pad-lg" style={{ color: "var(--danger)" }}>
            {state.error}
          </div>
        )}

        {!state.loading && !state.error && sorted.length === 0 && (
          <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
            {q ? (
              <>No groups match "<b>{q}</b>".</>
            ) : (
              <>No groups in your HOA yet. <b>Start the first one</b> with the button above.</>
            )}
          </div>
        )}

        {!state.loading && !state.error && sorted.length > 0 && (
          <div className="sc-grid">
            {sorted.map((g) => (
              <SubCommunityCard
                key={g.id}
                group={g}
                onOpen={() => onOpen(g.id)}
                onJoined={() => { load(); if (onJoined) onJoined(); }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function SubCommunityCard({ group, onOpen, onJoined }) {
  const [acting, setActing] = useStateSC(false);
  const [error, setError] = useStateSC(null);
  const viewer = group.viewer || {};

  const requestJoin = (e) => {
    // Prevent the card-click handler from also opening the detail view.
    e.stopPropagation();
    if (acting) return;
    setActing(true); setError(null);
    window.Api.subCommunityJoin(group.id)
      .then(() => { if (onJoined) onJoined(); })
      .catch((err) => setError(err.message || "Couldn't send request."))
      .finally(() => setActing(false));
  };

  return (
    <div
      className="sc-card"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        // Guard: only act on keystrokes that target the card itself.
        // Without this, tabbing to the nested "Request to join"
        // button and pressing Enter or Space bubbles up to the card
        // and triggers `onOpen()` in addition to the button's own
        // activation — keyboard users got both the join AND a jump
        // to detail view (codex P3 on PR #219). Also preventDefault
        // on Space so the page doesn't scroll.
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="sc-card__title">{group.name}</div>
      {group.description && (
        <div className="sc-card__desc">{group.description}</div>
      )}
      <div className="sc-card__meta">
        <span>{group.member_count} {group.member_count === 1 ? "member" : "members"}</span>
        <span>·</span>
        <span>created {_relTimeSC(group.created_at)}</span>
      </div>
      <div className="sc-card__action">
        {viewer.is_member ? (
          <span className="pill pill--ok">
            {viewer.role === "creator" ? "Creator"
              : viewer.role === "moderator" ? "Moderator"
              : "Joined"}
          </span>
        ) : viewer.has_pending_request ? (
          <span className="pill">Request pending</span>
        ) : (
          <button
            className="btn btn--ghost btn--sm"
            onClick={requestJoin}
            disabled={acting}
          >
            {acting ? "Sending…" : "Request to join"}
          </button>
        )}
        {error && <span className="text-sm" style={{ color: "var(--danger)", marginLeft: 8 }}>{error}</span>}
      </div>
    </div>
  );
}


// ── Create new ───────────────────────────────────────────────────────

function SubCommunityNew({ user, onCancel, onCreated }) {
  const [name, setName] = useStateSC("");
  const [description, setDescription] = useStateSC("");
  const [submitting, setSubmitting] = useStateSC(false);
  const [error, setError] = useStateSC(null);

  const submit = () => {
    if (submitting) return;
    setSubmitting(true); setError(null);
    window.Api.subCommunityCreate({ name: name.trim(), description: description.trim() })
      .then((grp) => { if (onCreated) onCreated(grp); })
      .catch((err) => setError(err.message || "Couldn't create group."))
      .finally(() => setSubmitting(false));
  };

  const canSubmit = name.trim().length > 0 && !submitting;

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 600 }}>
        <button className="btn btn--ghost" onClick={onCancel} style={{ alignSelf: "flex-start" }}>
          <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> Cancel
        </button>
        <h1 className="display" style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>
          Start a sub-community
        </h1>
        <div className="text-sm text-mute" style={{ marginTop: -8 }}>
          You become the creator. You can promote other members as moderators after they join.
        </div>

        <div className="card card--pad-lg" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label htmlFor="sc-new-name" className="sc-compose__label">Name</label>
            <input
              id="sc-new-name"
              className="input"
              placeholder="Knitting Club, Book Group, Cycling…"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              disabled={submitting}
            />
          </div>
          <div>
            <label htmlFor="sc-new-desc" className="sc-compose__label">Description (optional)</label>
            <textarea
              id="sc-new-desc"
              className="textarea"
              rows={5}
              placeholder="What's the group about? When/where do you meet? Who's it for?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              disabled={submitting}
            />
          </div>
          {error && <div className="text-sm" style={{ color: "var(--danger, #c0392b)" }}>{error}</div>}
          <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
            <button className="btn btn--ghost" onClick={onCancel} disabled={submitting}>Cancel</button>
            <button className="btn btn--primary" disabled={!canSubmit} onClick={submit}>
              {submitting ? "Creating…" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Detail (bulletin board lives here; group chat lands in slice 4) ──

function SubCommunityDetail({ user, publicId, onBack, onMembershipChange }) {
  const [state, setState] = useStateSC({ loading: true, error: null, group: null });
  const [acting, setActing] = useStateSC(false);
  const [actionError, setActionError] = useStateSC(null);

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    window.Api.subCommunity(publicId)
      .then((group) => setState({ loading: false, error: null, group }))
      .catch((err) => setState({
        loading: false,
        error: err.message || "Couldn't load group.",
        group: null,
      }));
  };
  useEffectSC(() => { load(); }, [publicId]);

  const requestJoin = () => {
    if (acting) return;
    setActing(true); setActionError(null);
    window.Api.subCommunityJoin(publicId)
      .then(() => { load(); if (onMembershipChange) onMembershipChange(); })
      .catch((err) => setActionError(err.message || "Couldn't send request."))
      .finally(() => setActing(false));
  };

  const leave = () => {
    if (acting) return;
    if (!window.confirm("Leave this group?")) return;
    setActing(true); setActionError(null);
    window.Api.subCommunityLeave(publicId)
      .then(() => { if (onMembershipChange) onMembershipChange(); onBack(); })
      .catch((err) => setActionError(err.message || "Couldn't leave."))
      .finally(() => setActing(false));
  };

  if (state.loading) {
    return (
      <div className="scroll">
        <div className="stack-md" style={{ maxWidth: 760 }}>
          <button className="btn btn--ghost" onClick={onBack} style={{ alignSelf: "flex-start" }}>
            <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> All Sub-Communities
          </button>
          <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
            Loading…
          </div>
        </div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="scroll">
        <div className="stack-md" style={{ maxWidth: 760 }}>
          <button className="btn btn--ghost" onClick={onBack} style={{ alignSelf: "flex-start" }}>
            <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> All Sub-Communities
          </button>
          <div className="card card--pad-lg" style={{ color: "var(--danger)" }}>
            {state.error}
          </div>
        </div>
      </div>
    );
  }

  const grp = state.group;
  const viewer = grp.viewer || {};
  const isCreator = viewer.role === "creator";

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 760 }}>
        <button className="btn btn--ghost" onClick={onBack} style={{ alignSelf: "flex-start" }}>
          <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> All Sub-Communities
        </button>

        <div className="card card--pad-lg">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 className="display" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>
                {grp.name}
              </h1>
              <div className="text-sm text-mute" style={{ marginTop: 6 }}>
                {grp.member_count} {grp.member_count === 1 ? "member" : "members"}
                {grp.creator?.display_name && (
                  <> · created by {grp.creator.display_name}</>
                )}
              </div>
            </div>
            <div className="row" style={{ gap: 8, alignItems: "center" }}>
              {viewer.is_member ? (
                <>
                  <span className="pill pill--ok">
                    {viewer.role === "creator" ? "Creator"
                      : viewer.role === "moderator" ? "Moderator"
                      : "Joined"}
                  </span>
                  {!isCreator && (
                    <button className="btn btn--ghost btn--sm" onClick={leave} disabled={acting}>
                      {acting ? "Leaving…" : "Leave"}
                    </button>
                  )}
                </>
              ) : viewer.has_pending_request ? (
                <span className="pill">Request pending</span>
              ) : (
                <button className="btn btn--primary" onClick={requestJoin} disabled={acting}>
                  {acting ? "Sending…" : "Request to join"}
                </button>
              )}
            </div>
          </div>
          {grp.description && (
            <p style={{ marginTop: 16, whiteSpace: "pre-wrap", color: "var(--ink-2)" }}>
              {grp.description}
            </p>
          )}
          {actionError && (
            <div className="text-sm" style={{ color: "var(--danger)", marginTop: 8 }}>{actionError}</div>
          )}
        </div>

        {viewer.is_member && (
          <>
            {(viewer.role === "creator" || viewer.role === "moderator") && (
              <SubCommunityManage
                publicId={publicId}
                viewerRole={viewer.role}
                initialPendingCount={viewer.pending_request_count || 0}
                onMembershipChange={() => { load(); if (onMembershipChange) onMembershipChange(); }}
                onGroupGone={() => {
                  // The group was disbanded — refresh /me so the
                  // sidebar drops the entry, then navigate back to
                  // the list rather than render a 404 in place.
                  if (onMembershipChange) onMembershipChange();
                  onBack();
                }}
              />
            )}
            <SubCommunityBulletin publicId={publicId} viewerRole={viewer.role} />
            <SubCommunityChat publicId={publicId} viewerRole={viewer.role} />
          </>
        )}
        {!viewer.is_member && (
          <div className="card card--pad-lg" style={{ color: "var(--mute)" }}>
            Join the group to participate. Once the creator approves your request, you'll see the bulletin board and chat.
          </div>
        )}
      </div>
    </div>
  );
}


// ── Bulletin board (slice 3) ─────────────────────────────────────────
//
// Members-only. Compose form at the top, posts list below (pinned
// first, then newest). Per-post inline controls: author can edit /
// delete; moderator (creator + mod) can additionally pin / unpin and
// delete anyone's post. Plain text body for v1 — no markdown
// rendering, no replies; the running group chat ships in slice 4.

function SubCommunityBulletin({ publicId, viewerRole }) {
  const [state, setState] = useStateSC({
    loading: true, error: null, posts: [], hasMore: false,
    loadingMore: false, cursor: null,
  });
  const isMod = viewerRole === "creator" || viewerRole === "moderator";

  // Cursor = id of the last UNPINNED row in the RAW server response —
  // never derived from the deduped render list. If a mass-unpin
  // floods the timeline with rows already rendered in the pinned
  // section, the dedup filter strips them from display but the cursor
  // still advances past them; deriving it from the rendered list
  // would repeat the same request forever (codex round-6 P2 on
  // PR #427).
  const _tailCursor = (rows, fallback) => {
    for (let i = rows.length - 1; i >= 0; i--) {
      if (!rows[i].is_pinned) return rows[i].id;
    }
    return fallback;
  };

  // Reload from the top (compose / edit / delete / pin all funnel here
  // so a mutation can't leave a stale multi-page buffer on screen).
  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    window.Api.subCommunityPosts(publicId)
      .then((data) => {
        const rows = data.posts || [];
        setState({
          loading: false, error: null,
          posts: rows,
          hasMore: !!data.has_more,
          loadingMore: false,
          cursor: _tailCursor(rows, null),
        });
      })
      .catch((err) => setState({
        loading: false,
        error: err.message || "Couldn't load posts.",
        posts: [], hasMore: false, loadingMore: false, cursor: null,
      }));
  };

  // Append the next-older timeline page (issue #417). Pinned posts
  // arrive in full on page 1 and never paginate, so the cursor's
  // (created_at, id) coordinates are immutable and pin flips can't
  // reshuffle pages (codex rounds 1-6 on PR #427). The id-dedup
  // filter only affects what renders, never the cursor.
  const loadMore = () => {
    // Re-entry guard: a double-click before React re-renders the
    // disabled button would fire two fetches for the same cursor and
    // append duplicate posts.
    if (state.loadingMore || !state.cursor) return;
    setState((s) => ({ ...s, loadingMore: true }));
    window.Api.subCommunityPosts(publicId, state.cursor)
      .then((data) => setState((s) => {
        const rows = data.posts || [];
        const seen = new Set(s.posts.map((p) => p.id));
        return {
          ...s,
          posts: [...s.posts, ...rows.filter((p) => !seen.has(p.id))],
          hasMore: !!data.has_more,
          loadingMore: false,
          cursor: _tailCursor(rows, s.cursor),
        };
      }))
      .catch(() => setState((s) => ({ ...s, loadingMore: false })));
  };
  useEffectSC(() => { load(); }, [publicId]);

  return (
    <>
      <SubCommunityPostCompose publicId={publicId} onCreated={load} />
      {state.loading && (
        <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
          Loading posts…
        </div>
      )}
      {state.error && (
        <div className="card card--pad-lg" style={{ color: "var(--danger)" }}>{state.error}</div>
      )}
      {!state.loading && !state.error && state.posts.length === 0 && (
        <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
          No posts yet. Start the bulletin board with the first one above.
        </div>
      )}
      {!state.loading && !state.error && state.posts.length > 0 && (
        <div className="sc-posts">
          {state.posts.map((p) => (
            <SubCommunityPost
              key={p.id}
              publicId={publicId}
              post={p}
              isMod={isMod}
              onChanged={load}
            />
          ))}
          {state.hasMore && (
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <button
                className="btn btn--ghost btn--sm"
                disabled={state.loadingMore}
                onClick={loadMore}
              >
                {state.loadingMore ? "Loading…" : "Load older posts"}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}


function SubCommunityPostCompose({ publicId, onCreated }) {
  const [title, setTitle] = useStateSC("");
  const [body, setBody] = useStateSC("");
  const [open, setOpen] = useStateSC(false);
  const [submitting, setSubmitting] = useStateSC(false);
  const [error, setError] = useStateSC(null);

  const submit = () => {
    if (submitting) return;
    setSubmitting(true); setError(null);
    window.Api.subCommunityCreatePost(publicId, { title: title.trim(), body: body.trim() })
      .then(() => {
        setTitle(""); setBody(""); setOpen(false);
        if (onCreated) onCreated();
      })
      .catch((err) => setError(err.message || "Couldn't post."))
      .finally(() => setSubmitting(false));
  };

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && !submitting;

  if (!open) {
    return (
      <button
        type="button"
        className="card card--pad-lg sc-post-stub"
        onClick={() => setOpen(true)}
        style={{ textAlign: "left", cursor: "pointer", width: "100%" }}
      >
        <Icon name="spark" size={14} /> <span>Post to the bulletin board…</span>
      </button>
    );
  }
  return (
    <div className="card card--pad-lg" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <input
        className="input"
        placeholder="Title (e.g. Saturday hike at 8 AM)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={200}
        disabled={submitting}
        aria-label="Post title"
      />
      <textarea
        className="textarea"
        rows={5}
        placeholder="What do you want to share with the group?"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={10_000}
        disabled={submitting}
        aria-label="Post body"
      />
      {error && <div className="text-sm" style={{ color: "var(--danger)" }}>{error}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
        <button
          className="btn btn--ghost"
          onClick={() => { setOpen(false); setTitle(""); setBody(""); setError(null); }}
          disabled={submitting}
        >Cancel</button>
        <button className="btn btn--primary" disabled={!canSubmit} onClick={submit}>
          {submitting ? "Posting…" : "Post"}
        </button>
      </div>
    </div>
  );
}


function SubCommunityPost({ publicId, post, isMod, onChanged }) {
  const [editing, setEditing] = useStateSC(false);
  // Draft state is initialized at mount time but the `post` prop can
  // change underneath us when the parent re-fetches (e.g., another
  // member edited it, or we just refreshed after a delete). Seeding
  // drafts from current props ONLY at Edit-click time guarantees the
  // editor always opens with the freshest post body, no matter how
  // long ago the component mounted (codex P2 on PR #220).
  const [draftTitle, setDraftTitle] = useStateSC("");
  const [draftBody, setDraftBody] = useStateSC("");
  const [acting, setActing] = useStateSC(false);
  const [error, setError] = useStateSC(null);

  const v = post.viewer || {};

  const startEdit = () => {
    setDraftTitle(post.title);
    setDraftBody(post.body);
    setError(null);
    setEditing(true);
  };

  const saveEdit = () => {
    if (acting) return;
    setActing(true); setError(null);
    window.Api.subCommunityUpdatePost(publicId, post.id, {
      title: draftTitle.trim(), body: draftBody.trim(),
    })
      .then(() => { setEditing(false); if (onChanged) onChanged(); })
      .catch((err) => setError(err.message || "Couldn't save."))
      .finally(() => setActing(false));
  };

  const togglePin = () => {
    if (acting) return;
    setActing(true); setError(null);
    window.Api.subCommunityUpdatePost(publicId, post.id, { is_pinned: !post.is_pinned })
      .then(() => { if (onChanged) onChanged(); })
      .catch((err) => setError(err.message || "Couldn't pin."))
      .finally(() => setActing(false));
  };

  const deletePost = () => {
    if (acting) return;
    if (!window.confirm("Delete this post?")) return;
    setActing(true); setError(null);
    window.Api.subCommunityDeletePost(publicId, post.id)
      .then(() => { if (onChanged) onChanged(); })
      .catch((err) => setError(err.message || "Couldn't delete."))
      .finally(() => setActing(false));
  };

  if (editing) {
    return (
      <div className="card card--pad-lg" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <input
          className="input"
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          maxLength={200}
          disabled={acting}
        />
        <textarea
          className="textarea"
          rows={5}
          value={draftBody}
          onChange={(e) => setDraftBody(e.target.value)}
          maxLength={10_000}
          disabled={acting}
        />
        {error && <div className="text-sm" style={{ color: "var(--danger)" }}>{error}</div>}
        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button
            className="btn btn--ghost"
            onClick={() => {
              setEditing(false);
              setError(null);
              // Drafts get re-seeded from current props on next Edit
              // click via startEdit() — no need to reset them here.
            }}
            disabled={acting}
          >Cancel</button>
          <button className="btn btn--primary" onClick={saveEdit} disabled={acting}>
            {acting ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`card card--pad-lg sc-post ${post.is_pinned ? "sc-post--pinned" : ""}`}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {post.is_pinned && <span className="pill" style={{ marginRight: 8 }}>📌 Pinned</span>}
          <span className="sc-post__title">{post.title}</span>
          <div className="text-sm text-mute" style={{ marginTop: 4 }}>
            {post.author?.display_name || "Member"} · {_relTimeSC(post.created_at)}
            {_wasEditedSC(post) && <> · edited</>}
          </div>
        </div>
        <div className="row" style={{ gap: 4 }}>
          {v.can_pin && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={togglePin}
              disabled={acting}
              title={post.is_pinned ? "Unpin" : "Pin"}
            >{post.is_pinned ? "Unpin" : "Pin"}</button>
          )}
          {v.can_edit && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={startEdit}
              disabled={acting}
            >Edit</button>
          )}
          {v.can_delete && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={deletePost}
              disabled={acting}
              style={{ color: "var(--danger)" }}
            >Delete</button>
          )}
        </div>
      </div>
      <div style={{ marginTop: 12, whiteSpace: "pre-wrap", color: "var(--ink-2)" }}>
        {post.body}
      </div>
      {error && <div className="text-sm" style={{ color: "var(--danger)", marginTop: 8 }}>{error}</div>}
    </div>
  );
}


// ── Group chat (slice 4) ─────────────────────────────────────────────
//
// Async by design — Mike's spec: "leave a message, someone else can see
// it later". No realtime push. Polls the API every CHAT_POLL_MS while
// the panel is visible (driven by document.visibilityState) so an idle
// background tab doesn't burn requests.
//
// Layout: scrollable history (newest at bottom, like every chat ever)
// with a "Load older messages" button when has_more is true, and a
// fixed compose row pinned to the bottom of the panel. Author can edit
// or delete their own message; moderators can only delete.

const CHAT_PAGE_SIZE = 50;
const CHAT_POLL_MS = 15_000;


function SubCommunityChat({ publicId, viewerRole }) {
  // `messages` is stored OLDEST-FIRST so we can render top-to-bottom.
  // The API returns newest-first per page — we reverse on insertion
  // so the DOM order matches reading order.
  const [messages, setMessages] = useStateSC([]);
  const [hasOlder, setHasOlder] = useStateSC(false);
  const [loading, setLoading] = useStateSC(true);
  const [error, setError] = useStateSC(null);
  const [loadingOlder, setLoadingOlder] = useStateSC(false);
  const scrollRef = useRefSC(null);
  const isMod = viewerRole === "creator" || viewerRole === "moderator";

  // Pull the latest page and reconcile it into the existing buffer.
  // On `initial=true` we replace outright (no pre-existing history
  // to preserve). On polls we MERGE: existing messages keep their
  // place, polled messages with the same id replace in place (so
  // edits / soft-deletes fan out), and polled-but-not-in-buffer
  // messages get appended. Without the merge, the buffer would
  // shrink back to the latest page on every poll — discarding any
  // history the user pulled in via "Load older messages" and
  // making infinite scroll-up unreliable (codex P2 #1 on PR #221).
  const refreshLatest = (initial = false) => {
    window.Api.subCommunityChat(publicId, { limit: CHAT_PAGE_SIZE })
      .then((data) => {
        const ordered = [...(data.messages || [])].reverse();
        const wasAtBottom = _isAtBottom(scrollRef.current);
        if (initial) {
          setMessages(ordered);
          setHasOlder(Boolean(data.has_more));
        } else {
          setMessages((cur) => _mergePolledChat(cur, ordered));
          // Don't touch hasOlder on poll — it's only meaningful
          // relative to the OLDEST message currently in the buffer,
          // which the loadOlder() path is the source of truth for.
          // Overwriting it from the polled page (which is anchored
          // on the newest end) could falsely report "no more" when
          // the user has older history loaded above.
        }
        // Recovery: clear any banner from a prior failed poll
        // (codex P2 #2 on PR #221).
        setError(null);
        if (initial || wasAtBottom) {
          requestAnimationFrame(() => _scrollToBottom(scrollRef.current));
        }
      })
      .catch((err) => setError(err.message || "Couldn't load chat."))
      .finally(() => setLoading(false));
  };

  // Mount + tab-becomes-visible + polling on visible interval.
  useEffectSC(() => {
    refreshLatest(true);
    const onVisibility = () => {
      if (document.visibilityState === "visible") refreshLatest(false);
    };
    document.addEventListener("visibilitychange", onVisibility);
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") refreshLatest(false);
    }, CHAT_POLL_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId]);

  const loadOlder = () => {
    if (loadingOlder || messages.length === 0) return;
    setLoadingOlder(true); setError(null);
    const cursor = messages[0].id;
    // Capture pre-load scroll geometry so we can keep the user's
    // visual position stable after older messages are prepended.
    const el = scrollRef.current;
    const prevScrollHeight = el ? el.scrollHeight : 0;
    const prevScrollTop = el ? el.scrollTop : 0;
    window.Api.subCommunityChat(publicId, { before: cursor, limit: CHAT_PAGE_SIZE })
      .then((data) => {
        const olderOldestFirst = [...(data.messages || [])].reverse();
        setMessages((cur) => [...olderOldestFirst, ...cur]);
        setHasOlder(Boolean(data.has_more));
        // After the DOM grows from the top, restore scrollTop so the
        // viewport stays anchored on what the user was reading.
        requestAnimationFrame(() => {
          if (!scrollRef.current) return;
          const grew = scrollRef.current.scrollHeight - prevScrollHeight;
          scrollRef.current.scrollTop = prevScrollTop + grew;
        });
      })
      .catch((err) => setError(err.message || "Couldn't load older messages."))
      .finally(() => setLoadingOlder(false));
  };

  const onSent = (newMsg) => {
    setMessages((cur) => [...cur, newMsg]);
    requestAnimationFrame(() => _scrollToBottom(scrollRef.current));
  };

  const onMessageChanged = (updated) => {
    setMessages((cur) => cur.map((m) => (m.id === updated.id ? updated : m)));
  };

  const onMessageDeleted = (deletedId) => {
    // Server keeps the row + tombstones the body; mirror that locally
    // so the chronology stays intact.
    setMessages((cur) =>
      cur.map((m) =>
        m.id === deletedId
          ? { ...m, is_deleted: true, body: "[message deleted]", viewer: { can_edit: false, can_delete: false } }
          : m,
      ),
    );
  };

  return (
    <div className="card card--pad-lg sc-chat">
      <div className="sc-chat__header">
        <Icon name="chat" size={16} /> <b>Group chat</b>
        <span className="text-sm text-mute" style={{ marginLeft: 8 }}>
          Leave a message — your group will see it later.
        </span>
      </div>

      <div className="sc-chat__history" ref={scrollRef}>
        {loading && (
          <div className="text-sm text-mute" style={{ textAlign: "center", padding: 16 }}>
            Loading messages…
          </div>
        )}
        {!loading && messages.length === 0 && !error && (
          <div className="text-sm text-mute" style={{ textAlign: "center", padding: 16 }}>
            No messages yet. Be the first to say something.
          </div>
        )}
        {error && (
          <div className="text-sm" style={{ color: "var(--danger)", padding: 8 }}>{error}</div>
        )}
        {hasOlder && (
          <div style={{ textAlign: "center", padding: 8 }}>
            <button
              className="btn btn--ghost btn--sm"
              onClick={loadOlder}
              disabled={loadingOlder}
            >
              {loadingOlder ? "Loading…" : "Load older messages"}
            </button>
          </div>
        )}
        {messages.map((m) => (
          <SubCommunityChatRow
            key={m.id}
            publicId={publicId}
            message={m}
            isMod={isMod}
            onChanged={onMessageChanged}
            onDeleted={onMessageDeleted}
          />
        ))}
      </div>

      <SubCommunityChatCompose publicId={publicId} onSent={onSent} />
    </div>
  );
}


function _isAtBottom(el) {
  if (!el) return true;
  // 40px slack so a near-bottom reader still counts as "tracking
  // newest" — keystroke jitter on mobile shouldn't strand them.
  return (el.scrollHeight - el.scrollTop - el.clientHeight) < 40;
}

function _scrollToBottom(el) {
  if (!el) return;
  el.scrollTop = el.scrollHeight;
}

// Reconcile a polled page into the existing buffer.
//   cur    — buffer, oldest-first
//   polled — newest page from the server, oldest-first
// Returns a new buffer, oldest-first:
//   - In-place replacement for messages whose id appears in both
//     (server is authoritative — picks up edits + soft-delete
//     tombstones).
//   - Polled messages whose id isn't in the buffer are appended.
//   - Older history that's in the buffer but NOT in the polled
//     page is preserved (this is the whole point — codex P2 #1).
// Final sort by created_at + id keeps ordering stable even when
// the appended messages land between existing ones (clock skew is
// uncommon but possible).
function _mergePolledChat(cur, polled) {
  if (!polled || polled.length === 0) return cur;
  const polledById = new Map(polled.map((m) => [m.id, m]));
  const seenIds = new Set();
  const merged = [];
  for (const m of cur) {
    merged.push(polledById.has(m.id) ? polledById.get(m.id) : m);
    seenIds.add(m.id);
  }
  for (const m of polled) {
    if (!seenIds.has(m.id)) merged.push(m);
  }
  merged.sort((a, b) => {
    const ta = new Date(a.created_at).getTime();
    const tb = new Date(b.created_at).getTime();
    if (ta !== tb) return ta - tb;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  return merged;
}


function SubCommunityChatCompose({ publicId, onSent }) {
  const [draft, setDraft] = useStateSC("");
  const [sending, setSending] = useStateSC(false);
  const [error, setError] = useStateSC(null);

  const send = () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true); setError(null);
    window.Api.subCommunityCreateChat(publicId, body)
      .then((msg) => {
        setDraft("");
        if (onSent) onSent(msg);
      })
      .catch((err) => setError(err.message || "Couldn't send."))
      .finally(() => setSending(false));
  };

  const onKey = (e) => {
    // Enter sends; Shift+Enter inserts a newline. Mirrors most chat
    // clients so muscle memory works.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="sc-chat__compose">
      <textarea
        className="textarea sc-chat__input"
        rows={2}
        placeholder="Leave a message for the group… (Enter to send, Shift+Enter for newline)"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        maxLength={5000}
        disabled={sending}
        aria-label="Chat message"
      />
      <button
        className="btn btn--primary"
        onClick={send}
        disabled={sending || draft.trim().length === 0}
      >
        {sending ? "Sending…" : "Send"}
      </button>
      {error && (
        <div className="text-sm" style={{ color: "var(--danger)", flexBasis: "100%" }}>{error}</div>
      )}
    </div>
  );
}


function SubCommunityChatRow({ publicId, message, isMod, onChanged, onDeleted }) {
  const [editing, setEditing] = useStateSC(false);
  const [draft, setDraft] = useStateSC("");
  const [acting, setActing] = useStateSC(false);
  const [error, setError] = useStateSC(null);

  const v = message.viewer || {};

  const startEdit = () => {
    setDraft(message.body);
    setError(null);
    setEditing(true);
  };

  const saveEdit = () => {
    const body = draft.trim();
    if (!body || acting) return;
    setActing(true); setError(null);
    window.Api.subCommunityUpdateChat(publicId, message.id, body)
      .then((updated) => {
        setEditing(false);
        if (onChanged) onChanged(updated);
      })
      .catch((err) => setError(err.message || "Couldn't save."))
      .finally(() => setActing(false));
  };

  const remove = () => {
    if (acting) return;
    if (!window.confirm("Delete this message?")) return;
    setActing(true); setError(null);
    window.Api.subCommunityDeleteChat(publicId, message.id)
      .then(() => { if (onDeleted) onDeleted(message.id); })
      .catch((err) => setError(err.message || "Couldn't delete."))
      .finally(() => setActing(false));
  };

  return (
    <div className={`sc-chat__row ${message.is_deleted ? "sc-chat__row--deleted" : ""}`}>
      <div className="sc-chat__meta">
        <span className="sc-chat__author">{message.author?.display_name || "Member"}</span>
        <span className="sc-chat__time">{_estTimeSC(message.created_at)}</span>
        {message.edited_at && !message.is_deleted && (
          <span className="sc-chat__edited">edited</span>
        )}
      </div>
      {editing ? (
        <div className="sc-chat__edit">
          <textarea
            className="textarea"
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={5000}
            disabled={acting}
            aria-label="Edit chat message"
          />
          <div className="row" style={{ gap: 6, justifyContent: "flex-end", marginTop: 6 }}>
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => { setEditing(false); setError(null); }}
              disabled={acting}
            >Cancel</button>
            <button
              className="btn btn--primary btn--sm"
              onClick={saveEdit}
              disabled={acting || draft.trim().length === 0}
            >
              {acting ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ) : (
        <div className="sc-chat__body">{message.body}</div>
      )}
      {error && <div className="text-sm" style={{ color: "var(--danger)", marginTop: 4 }}>{error}</div>}
      {!editing && !message.is_deleted && (v.can_edit || v.can_delete) && (
        <div className="sc-chat__row-actions">
          {v.can_edit && (
            <button className="sc-chat__action" onClick={startEdit} disabled={acting} title="Edit">
              Edit
            </button>
          )}
          {v.can_delete && (
            <button className="sc-chat__action sc-chat__action--danger" onClick={remove} disabled={acting} title="Delete">
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// ── Moderation panel (slice 5) ───────────────────────────────────────
//
// Visible only to creator + moderators. Two sections — Pending Join
// Requests (approve / deny) and Members (promote / demote / remove).
// Both use native <details> for collapsibility; pending requests
// auto-expands when count > 0 so action items don't hide.
//
// Permissions mirror the backend (which is authoritative — these are
// just UX hints):
//   - Approve / deny: creator OR moderator
//   - Promote member → moderator, demote moderator → member: CREATOR only
//   - Remove member: creator (anyone except themselves) OR moderator
//     (members only, not other moderators or the creator)


function SubCommunityManage({ publicId, viewerRole, initialPendingCount, onMembershipChange, onGroupGone }) {
  const [requests, setRequests] = useStateSC([]);
  const [members, setMembers] = useStateSC([]);
  const [loadingRequests, setLoadingRequests] = useStateSC(true);
  const [loadingMembers, setLoadingMembers] = useStateSC(true);
  const [reqError, setReqError] = useStateSC(null);
  const [memError, setMemError] = useStateSC(null);
  const isCreator = viewerRole === "creator";

  const loadRequests = () => {
    setLoadingRequests(true); setReqError(null);
    window.Api.subCommunityRequests(publicId)
      .then((data) => setRequests(data.requests || []))
      .catch((err) => setReqError(err.message || "Couldn't load join requests."))
      .finally(() => setLoadingRequests(false));
  };
  const loadMembers = () => {
    setLoadingMembers(true); setMemError(null);
    window.Api.subCommunityMembers(publicId)
      .then((data) => setMembers(data.members || []))
      .catch((err) => setMemError(err.message || "Couldn't load members."))
      .finally(() => setLoadingMembers(false));
  };

  useEffectSC(() => {
    loadRequests();
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId]);

  const reloadAll = () => {
    loadRequests();
    loadMembers();
    if (onMembershipChange) onMembershipChange();
  };

  const pendingCount = requests.length;

  return (
    <div className="card card--pad-lg sc-manage">
      <div className="sc-manage__title">
        <Icon name="settings" size={16} /> <b>Manage group</b>
        <span className="text-sm text-mute" style={{ marginLeft: 8 }}>
          Visible to creator + moderators.
        </span>
      </div>

      <details className="sc-manage__section" open={pendingCount > 0 || initialPendingCount > 0}>
        <summary className="sc-manage__summary">
          <span>Pending join requests</span>
          {pendingCount > 0 && <span className="sc-manage__badge">{pendingCount}</span>}
        </summary>
        {loadingRequests && (
          <div className="text-sm text-mute" style={{ padding: 8 }}>Loading…</div>
        )}
        {reqError && (
          <div className="text-sm" style={{ color: "var(--danger)", padding: 8 }}>{reqError}</div>
        )}
        {!loadingRequests && !reqError && requests.length === 0 && (
          <div className="text-sm text-mute" style={{ padding: 8 }}>
            No pending requests. New requests show up here.
          </div>
        )}
        {!loadingRequests && !reqError && requests.length > 0 && (
          <div className="sc-requests">
            {requests.map((r) => (
              <SubCommunityRequestRow
                key={r.id}
                publicId={publicId}
                request={r}
                onResolved={reloadAll}
              />
            ))}
          </div>
        )}
      </details>

      <details className="sc-manage__section">
        <summary className="sc-manage__summary">
          <span>Members</span>
          <span className="sc-manage__badge">{members.length}</span>
        </summary>
        {loadingMembers && (
          <div className="text-sm text-mute" style={{ padding: 8 }}>Loading…</div>
        )}
        {memError && (
          <div className="text-sm" style={{ color: "var(--danger)", padding: 8 }}>{memError}</div>
        )}
        {!loadingMembers && !memError && (
          <div className="sc-roster">
            {members.map((m) => (
              <SubCommunityMemberRow
                key={m.id || `${m.role}-${m.display_name}`}
                publicId={publicId}
                member={m}
                isCreator={isCreator}
                onChanged={reloadAll}
              />
            ))}
          </div>
        )}
      </details>

      {/* Creator-only group actions: transfer ownership + disband.
          Mods don't see this section. Sits last in the panel because
          these are heavy, infrequent actions; the section is closed
          by default so a mis-tap can't trigger a destructive flow. */}
      {isCreator && (
        <SubCommunityGroupActions
          publicId={publicId}
          members={members}
          onTransferred={onMembershipChange}
          onDisbanded={onGroupGone || onMembershipChange}
        />
      )}
    </div>
  );
}


function SubCommunityGroupActions({ publicId, members, onTransferred, onDisbanded }) {
  // Transfer candidates: existing members who aren't the creator
  // themselves AND whose account is still active. Inactive members
  // would be rejected by the backend with `target_inactive` — filter
  // here too so the dropdown doesn't list "User Deactivated" rows
  // (codex P1 on PR #227). `is_active !== false` lets pre-payload-
  // upgrade clients fall through to "treat as active" rather than
  // hiding everyone.
  const transferCandidates = (members || []).filter(
    (m) => m.role !== "creator" && m.is_active !== false,
  );
  return (
    <details className="sc-manage__section sc-manage__section--danger">
      <summary className="sc-manage__summary">
        <span>Group actions</span>
        <span className="text-sm text-mute" style={{ marginLeft: 8 }}>
          Transfer ownership or disband
        </span>
      </summary>
      <SubCommunityTransferControl
        publicId={publicId}
        candidates={transferCandidates}
        onTransferred={onTransferred}
      />
      <SubCommunityDisbandControl
        publicId={publicId}
        onDisbanded={onDisbanded}
      />
    </details>
  );
}


function SubCommunityTransferControl({ publicId, candidates, onTransferred }) {
  const [selected, setSelected] = useStateSC("");
  const [confirming, setConfirming] = useStateSC(false);
  const [acting, setActing] = useStateSC(false);
  const [error, setError] = useStateSC(null);
  const [success, setSuccess] = useStateSC(false);

  if (candidates.length === 0) {
    return (
      <div className="sc-actions__row">
        <div>
          <div style={{ fontWeight: 600 }}>Transfer ownership</div>
          <div className="text-sm text-mute">
            Invite another member into the group first — only existing members
            can take ownership.
          </div>
        </div>
      </div>
    );
  }

  const targetName = (() => {
    const c = candidates.find((c_) => String(c_.id) === String(selected));
    return c?.display_name || "this member";
  })();

  const transfer = () => {
    if (!selected || acting) return;
    setActing(true); setError(null);
    window.Api.subCommunityTransfer(publicId, Number(selected))
      .then(() => {
        setSuccess(true);
        setConfirming(false);
        if (onTransferred) onTransferred();
      })
      .catch((err) => setError(err.message || "Couldn't transfer ownership."))
      .finally(() => setActing(false));
  };

  return (
    <div className="sc-actions__row">
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>Transfer ownership</div>
        <div className="text-sm text-mute">
          Pass creator rights to another existing member. You'll become a
          moderator. The new owner needs an empty slot in their 5-group quota.
        </div>
        <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <select
            className="select"
            value={selected}
            onChange={(e) => { setSelected(e.target.value); setError(null); }}
            disabled={acting || success}
            aria-label="Choose new owner"
          >
            <option value="">Choose a member…</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name || "Member"}{c.role === "moderator" ? " (mod)" : ""}
              </option>
            ))}
          </select>
          {!confirming ? (
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => { if (selected) setConfirming(true); }}
              disabled={!selected || acting || success}
            >Transfer…</button>
          ) : (
            <>
              <span className="text-sm" style={{ alignSelf: "center" }}>
                Transfer to <b>{targetName}</b>?
              </span>
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => setConfirming(false)}
                disabled={acting}
              >Cancel</button>
              <button
                className="btn btn--primary btn--sm"
                onClick={transfer}
                disabled={acting}
              >{acting ? "Transferring…" : "Confirm transfer"}</button>
            </>
          )}
        </div>
        {error && (
          <div className="text-sm" style={{ color: "var(--danger)", marginTop: 6 }}>{error}</div>
        )}
        {success && (
          <div className="text-sm" style={{ color: "var(--ok, #2a9d4d)", marginTop: 6 }}>
            Ownership transferred. Refresh to see your new role.
          </div>
        )}
      </div>
    </div>
  );
}


function SubCommunityDisbandControl({ publicId, onDisbanded }) {
  const [typed, setTyped] = useStateSC("");
  const [confirming, setConfirming] = useStateSC(false);
  const [acting, setActing] = useStateSC(false);
  const [error, setError] = useStateSC(null);

  const disband = () => {
    if (typed.trim().toUpperCase() !== "DELETE" || acting) return;
    setActing(true); setError(null);
    window.Api.subCommunityDisband(publicId)
      .then(() => { if (onDisbanded) onDisbanded(); })
      .catch((err) => setError(err.message || "Couldn't disband group."))
      .finally(() => setActing(false));
  };

  return (
    <div className="sc-actions__row sc-actions__row--danger">
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, color: "var(--danger)" }}>Disband group</div>
        <div className="text-sm text-mute">
          Removes the group from everyone's sidebar. Members are notified.
          Bulletin posts and chat history are preserved but no longer
          reachable. This frees a slot in your 5-group quota.
        </div>
        {!confirming ? (
          <button
            className="btn btn--ghost btn--sm"
            style={{ color: "var(--danger)", marginTop: 8 }}
            onClick={() => setConfirming(true)}
          >Disband…</button>
        ) : (
          <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span className="text-sm">Type <b>DELETE</b> to confirm:</span>
            <input
              type="text"
              className="input"
              style={{ width: 120 }}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={acting}
              aria-label="Type DELETE to confirm"
            />
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => { setConfirming(false); setTyped(""); setError(null); }}
              disabled={acting}
            >Cancel</button>
            <button
              className="btn btn--primary btn--sm"
              style={{ background: "var(--danger)", borderColor: "var(--danger)" }}
              onClick={disband}
              disabled={acting || typed.trim().toUpperCase() !== "DELETE"}
            >{acting ? "Disbanding…" : "Disband group"}</button>
          </div>
        )}
        {error && (
          <div className="text-sm" style={{ color: "var(--danger)", marginTop: 6 }}>{error}</div>
        )}
      </div>
    </div>
  );
}


function SubCommunityRequestRow({ publicId, request, onResolved }) {
  const [acting, setActing] = useStateSC(false);
  const [error, setError] = useStateSC(null);

  const resolve = (action) => {
    if (acting) return;
    setActing(true); setError(null);
    window.Api.subCommunityResolveRequest(publicId, request.id, action)
      .then(() => { if (onResolved) onResolved(); })
      .catch((err) => setError(err.message || `Couldn't ${action} request.`))
      .finally(() => setActing(false));
  };

  return (
    <div className="sc-requests__row">
      <div className="sc-roster__name">
        <Icon name="users" size={14} /> {request.user?.display_name || "Member"}
      </div>
      <div className="text-sm text-mute" style={{ flex: 1 }}>
        Requested {_relTimeSC(request.created_at)}
      </div>
      <div className="row" style={{ gap: 6 }}>
        <button
          className="btn btn--ghost btn--sm"
          onClick={() => resolve("deny")}
          disabled={acting}
        >Deny</button>
        <button
          className="btn btn--primary btn--sm"
          onClick={() => resolve("approve")}
          disabled={acting}
        >Approve</button>
      </div>
      {error && (
        <div className="text-sm" style={{ color: "var(--danger)", flexBasis: "100%" }}>
          {error}
        </div>
      )}
    </div>
  );
}


function SubCommunityMemberRow({ publicId, member, isCreator, onChanged }) {
  const [acting, setActing] = useStateSC(false);
  const [error, setError] = useStateSC(null);
  const isMemberCreator = member.role === "creator";
  const isMod = member.role === "moderator";

  const setRole = (role) => {
    if (acting) return;
    setActing(true); setError(null);
    window.Api.subCommunityUpdateRole(publicId, member.id, role)
      .then(() => { if (onChanged) onChanged(); })
      .catch((err) => setError(err.message || "Couldn't update role."))
      .finally(() => setActing(false));
  };

  const remove = () => {
    if (acting) return;
    const confirmMsg = isMod
      ? `Remove moderator ${member.display_name || "this member"} from the group?`
      : `Remove ${member.display_name || "this member"} from the group?`;
    if (!window.confirm(confirmMsg)) return;
    setActing(true); setError(null);
    window.Api.subCommunityRemoveMember(publicId, member.id)
      .then(() => { if (onChanged) onChanged(); })
      .catch((err) => setError(err.message || "Couldn't remove."))
      .finally(() => setActing(false));
  };

  // Permission gate (mirrors backend so the UI doesn't dangle disabled
  // controls). Creator never sees actions for themselves.
  const canPromote = isCreator && !isMemberCreator && !isMod;
  const canDemote = isCreator && isMod;
  // Mods can remove plain members; creator can remove anyone but
  // themselves and the creator role itself (route blocks). The
  // viewer is at least a moderator (this whole panel is mod-gated),
  // so allow remove on non-creator rows for the creator, and only
  // on plain-member rows for a non-creator viewer.
  const viewerIsCreator = isCreator;
  const canRemove = !isMemberCreator && (viewerIsCreator || !isMod);

  return (
    <div className="sc-roster__row">
      <div className="sc-roster__name">
        <Icon name="users" size={14} /> {member.display_name || "Member"}
      </div>
      <span className={`pill ${isMemberCreator ? "pill--ok" : ""}`}>
        {isMemberCreator ? "Creator" : isMod ? "Moderator" : "Member"}
      </span>
      <div className="row" style={{ gap: 6, marginLeft: "auto" }}>
        {canPromote && (
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => setRole("moderator")}
            disabled={acting}
            title="Promote to moderator"
          >Promote</button>
        )}
        {canDemote && (
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => setRole("member")}
            disabled={acting}
            title="Demote to member"
          >Demote</button>
        )}
        {canRemove && (
          <button
            className="btn btn--ghost btn--sm"
            onClick={remove}
            disabled={acting}
            style={{ color: "var(--danger)" }}
            title="Remove from group"
          >Remove</button>
        )}
      </div>
      {error && (
        <div className="text-sm" style={{ color: "var(--danger)", flexBasis: "100%" }}>
          {error}
        </div>
      )}
    </div>
  );
}


export { SubCommunities };